import React from 'react';
import { arrayOf, func, node, number, object, shape, string } from 'prop-types';
import classNames from 'classnames';

import { NamedLink, ExternalLink, ContactUsModal } from '../../../../components';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { useConfiguration } from '../../../../context/configurationContext';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';

import footerLogo from '../../../../components/Logo/images/scaled512h.png';
import footerPaymentImage from '../../../../assets/footer-payment-img.png';

import css from './SectionFooter.module.css';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import { manageDisableScrolling } from '../../../../ducks/ui.duck';

// The number of columns (numberOfColumns) affects styling
const GRID_CONFIG = [
  { gridCss: css.gridCol1 },
  { gridCss: css.gridCol2 },
  { gridCss: css.gridCol3 },
  { gridCss: css.gridCol4 },
];

const getIndex = numberOfColumns => numberOfColumns - 1;

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

const updateSloganWithIntl = (slogan, intl) => ({
  ...slogan,
  content: intl.messages[`SectionFooter.slogan`]
    ? intl.formatMessage({
      id: 'SectionFooter.slogan',
    })
    : slogan,
});

const updateBlocksWithIntl = (blocks, intl) => {
  return blocks.map(block => ({
    ...block,
    text: {
      ...block.text,
      content: intl.messages[`SectionFooter.blocks.${block.blockId}`]
        ? intl.formatMessage({
          id: `SectionFooter.blocks.${block.blockId}`,
        })
        : block.text.content,
    },
  }));
};

// Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns,
    socialMediaLinks,
    slogan,
    appearance,
    copyright,
    blocks,
    options,
    linkLogoToExternalSite,
  } = props;

  const config = useConfiguration();
  const intl = useIntl();
  const dispatch = useDispatch();

  // State for Contact Us modal
  const [contactModalOpen, setContactModalOpen] = useState(false);

  const onManageDisableScrolling = (componentId, disable) =>
    dispatch(manageDisableScrolling(componentId, disable));

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;

  const logoComponent = linkLogoToExternalSite?.href ? (
    <ExternalLink className={css.logoLink} href={linkLogoToExternalSite.href} target="_self">
      <img className={css.logoImage} src={footerLogo} alt={config.marketplaceName} />
    </ExternalLink>
  ) : (
    <NamedLink className={css.logoLink} name="LandingPage">
      <img className={css.logoImage} src={footerLogo} alt={config.marketplaceName} />
    </NamedLink>
  );

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      contentClassName={css.containerContent}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footer}>
        <div className={css.content}>
          <div className={css.leftContent}>
            {logoComponent}
            <div className={css.detailsInfo}>
              <Field data={updateSloganWithIntl(slogan, intl)} className={css.slogan} />
              <Field data={copyright ? { ...copyright, content: copyright.content?.replace(/\d{4}/, new Date().getFullYear()) } : copyright} className={css.copyright} />
              {showSocialMediaLinks ? (
                <div className={css.icons}>
                  <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
                </div>
              ) : null}
              <img
                className={css.paymentImage}
                src={footerPaymentImage}
                alt={config.marketplaceName}
              />
              <span className={css.paymentMethods}>
                <FormattedMessage id="SectionFooter.paymentMethods" />
              </span>

              <span
                className={css.contactUs}
                onClick={() => setContactModalOpen(true)}
                style={{ cursor: 'pointer' }}
              >
                <FormattedMessage id="SectionFooter.contactUs" />
              </span>
            </div>
          </div>
          <div className={classNames(css.grid, getGridCss(numberOfColumns))}>
            <BlockBuilder
              blocks={updateBlocksWithIntl(blocks, intl)}
              sectionId={sectionId}
              options={options}
            />
          </div>
        </div>
      </div>

      {/* Contact Us Modal */}
      <ContactUsModal
        id="FooterContactUsModal"
        isOpen={contactModalOpen}
        onCloseModal={() => setContactModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </SectionContainer>
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionFooter.defaultProps = {
  className: null,
  rootClassName: null,
  textClassName: null,
  numberOfColumns: 1,
  socialMediaLinks: [],
  slogan: null,
  copyright: null,
  appearance: null,
  blocks: [],
  options: null,
};

SectionFooter.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  numberOfColumns: number,
  socialMediaLinks: arrayOf(object),
  slogan: object,
  copyright: object,
  appearance: object,
  blocks: arrayOf(object),
  options: propTypeOption,
};

export default SectionFooter;
